import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { router } from '@inertiajs/react';
import { Check, Users, User } from 'lucide-react';

type PoolState = 'in_pool' | 'placed' | 'inactive';

interface ApprovedProps {
  user: {
    admin_verified: boolean;
    admin_verified_at: string;
  };
  can_browse_mentees: boolean;
  has_active_match: boolean;
  // null for mentors and admins, who have no standing in the mentee pool.
  pool_state: PoolState | null;
  support_email: string;
}

export default function Approved({
  user,
  can_browse_mentees,
  has_active_match,
  pool_state,
  support_email,
}: ApprovedProps) {
  const approvedDate = new Date(user.admin_verified_at).toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );

  // A placed mentee is out of the pool for good, and an inactive one is hidden
  // from mentors, so neither may be told a mentor is on the way.
  const isInactive = !has_active_match && pool_state === 'inactive';

  const { heading: statusHeading, message: statusMessage } = (() => {
    if (has_active_match) {
      return {
        heading: 'Match Status',
        message: 'You have an active mentor connection',
      };
    }
    if (pool_state === 'placed') {
      return {
        heading: 'Mentorship Complete',
        message: 'You completed your mentorship. Congratulations!',
      };
    }
    if (pool_state === 'inactive') {
      return {
        heading: 'Matching Status',
        message: `Your application is inactive, so mentors can't see you right now. Email ${support_email} and we'll put you back in the matching pool.`,
      };
    }
    return {
      heading: 'Matching Status',
      message: "You're in the matching pool - a mentor will reach out soon",
    };
  })();

  return (
    <div className="min-h-screen py-8 bg-gray-50">
      <div className="max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-semibold">
                Application Status
              </CardTitle>
              <Badge className="bg-primary/10 text-primary hover:bg-primary/10">
                <Check className="h-3 w-3 mr-1" />
                Approved
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Timeline - All Steps Complete */}
            <div className="space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Application Journey</span>
                <span className="font-medium">{approvedDate}</span>
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-between">
                  <div className="bg-white pr-2">
                    <div className="h-8 w-8 rounded-full border-2 border-primary bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="bg-white px-2">
                    <div className="h-8 w-8 rounded-full border-2 border-primary bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="bg-white pl-2">
                    <div className="h-8 w-8 rounded-full border-2 border-primary bg-primary flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-primary">Submitted</span>
                <span className="text-primary">Reviewed</span>
                <span className="font-medium text-primary">Approved</span>
              </div>
            </div>

            {/* Welcome Message */}
            <div className="rounded-lg bg-primary/5 p-4 border border-primary/20">
              <h3 className="font-semibold text-primary mb-1">
                Welcome to Project Short!
              </h3>
              <p className="text-sm text-primary/80">
                You're now an active member of our community
              </p>
            </div>

            {/* Next Steps */}
            <div className="space-y-4">
              {can_browse_mentees ? (
                <>
                  <div className="rounded-lg bg-muted p-4">
                    <p className="text-sm font-medium mb-1">Ready to Mentor</p>
                    <p className="text-sm text-muted-foreground">
                      Browse available mentees and start making a difference
                    </p>
                  </div>
                  <Button
                    onClick={() => router.visit('/matches')}
                    className="w-full"
                    size="lg"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Browse Available Mentees
                  </Button>
                </>
              ) : (
                <>
                  <div
                    className={
                      isInactive
                        ? 'rounded-lg border border-amber-200 bg-amber-50 p-4'
                        : 'rounded-lg bg-muted p-4'
                    }
                  >
                    <p className="text-sm font-medium mb-1">
                      {statusHeading}
                    </p>
                    <p
                      className={
                        isInactive
                          ? 'text-sm text-amber-900'
                          : 'text-sm text-muted-foreground'
                      }
                    >
                      {statusMessage}
                    </p>
                  </div>
                  {/* Always shown: an inactive or placed mentee still needs a way
                      into their dashboard, which is where their history lives. */}
                  <Button
                    onClick={() => router.visit('/matches/dashboard')}
                    className="w-full"
                    size="lg"
                    variant={has_active_match ? 'default' : 'outline'}
                  >
                    <User className="h-4 w-4 mr-2" />
                    View My Dashboard
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
